import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, CheckCircle, Users, Briefcase, Shield,
  Star, MapPin, Loader2, ChevronDown, Sparkles, TrendingUp, Zap
} from 'lucide-react';
import { appName, copyright } from '../config/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('professional');
  const [formData, setFormData] = useState({
    type: 'professional',
    name: '',
    email: '',
    phone: '',
    business_name: '',
    service_type: '',
    location: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [regCount, setRegCount] = useState({ total: 0, professionals: 0, customers: 0 });
  const [services, setServices] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countRes, svcRes] = await Promise.all([
          axios.get(`${API}/register/count`),
          axios.get(`${API}/services/popular`)
        ]);
        setRegCount(countRes.data);
        setServices(svcRes.data);
      } catch (e) {
        console.error('Error:', e);
      }
    };
    fetchData();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData(prev => ({ ...prev, type: tab }));
    setError('');
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const selectService = (svc) => {
    setFormData(prev => ({ ...prev, service_type: svc.name }));
    setServiceSearch(svc.name);
    setShowServiceDropdown(false);
  };

  const filteredServices = serviceSearch.length > 0
    ? services.filter(s => s.name.toLowerCase().includes(serviceSearch.toLowerCase()))
    : services;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      setError('Please fill in all required fields.');
      return;
    }
    if (activeTab === 'professional' && !formData.service_type) {
      setError('Please select your service type.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/register`, formData);
      setSubmitted(true);
      setRegCount(prev => ({
        ...prev,
        total: prev.total + 1,
        [activeTab === 'professional' ? 'professionals' : 'customers']:
          prev[activeTab === 'professional' ? 'professionals' : 'customers'] + 1
      }));
    } catch (err) {
      if (err.response?.status === 409) {
        setError('This email is already registered. Thank you for your interest!');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Success Screen
  if (submitted) {
    const isPro = activeTab === 'professional';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8" data-testid="register-success-screen">
            <div className="w-20 h-20 rounded-full bg-[#37b34a]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#37b34a]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You're on the list!</h2>
            <p className="text-gray-500 mb-2">
              {isPro
                ? `Welcome to ${appName} — you're now a Founding Member. We'll be in touch when ${appName} is live.`
                : `We'll let you know when ${appName} is live. You'll be among the first to find top professionals!`}
            </p>

            <div className="bg-gray-50 rounded-xl p-4 mt-6 mb-6">
              <p className="text-sm text-gray-500 mb-1">Early registrations so far</p>
              <p className="text-3xl font-extrabold text-gray-900">{regCount.total.toLocaleString()}</p>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Share with others to help us build the best professional network in South Africa.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="register-back-home-btn"
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              Back to {appName}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-8 pb-12 md:pt-12 md:pb-16">
          {/* Top Nav */}
          <div className="flex items-center justify-between mb-12">
            <a href="/" className="inline-block">
              <svg width="180" height="32" viewBox="0 0 180 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <text x="0" y="26" fontFamily="Inter, sans-serif" fontSize="22" fontWeight="800" fill="#37b34a">{appName}</text>
              </svg>
            </a>
            <a
              href="/"
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              Back to home
            </a>
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            {/* Left - Copy */}
            <div className="md:pt-4">
              <div className="inline-flex items-center gap-2 bg-[#37b34a]/10 text-[#37b34a] text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                <Sparkles size={14} />
                Early Access — Limited Spots
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-[42px] font-extrabold text-gray-900 leading-tight mb-4">
                South Africa's new home for
                <span className="text-[#37b34a]"> trusted professionals</span>
              </h1>
              <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                Whether you're a skilled professional looking for clients or someone searching for quality services — register now and be first in line when we launch.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Users size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">{regCount.total.toLocaleString() || '0'}</p>
                    <p className="text-xs text-gray-500">People registered</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Briefcase size={18} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">100+</p>
                    <p className="text-xs text-gray-500">Service categories</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <MapPin size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">All SA</p>
                    <p className="text-xs text-gray-500">Nationwide coverage</p>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                {[
                  { icon: Zap, text: "Be first — early members get priority placement" },
                  { icon: Shield, text: "Free to register — no commitments, no fees" },
                  { icon: TrendingUp, text: "Grow your reach across South Africa" },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#37b34a]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={12} className="text-[#37b34a]" />
                    </div>
                    <p className="text-sm text-gray-600">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-gray-100">
                <button
                  onClick={() => handleTabChange('professional')}
                  className={`flex-1 py-4 text-sm font-semibold text-center transition-colors relative ${
                    activeTab === 'professional' ? 'text-[#37b34a]' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  I'm a Professional
                  {activeTab === 'professional' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#37b34a]" />
                  )}
                </button>
                <button
                  onClick={() => handleTabChange('customer')}
                  className={`flex-1 py-4 text-sm font-semibold text-center transition-colors relative ${
                    activeTab === 'customer' ? 'text-[#37b34a]' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  I need a service
                  {activeTab === 'customer' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#37b34a]" />
                  )}
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full name *</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Thabo Mokoena"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email address *</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="thabo@example.com"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
                  <input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g. 082 123 4567"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                  />
                </div>

                {activeTab === 'professional' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Business name</label>
                      <input
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleChange}
                        placeholder="e.g. Thabo's Plumbing Services"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                      />
                    </div>

                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service type *</label>
                      <div
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm cursor-pointer flex items-center justify-between hover:border-gray-300 transition-colors"
                        onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                      >
                        <span className={formData.service_type ? 'text-gray-900' : 'text-gray-400'}>
                          {formData.service_type || 'Select your service'}
                        </span>
                        <ChevronDown size={16} className="text-gray-400" />
                      </div>
                      {showServiceDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              placeholder="Search services..."
                              value={serviceSearch}
                              onChange={(e) => setServiceSearch(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#37b34a]"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {filteredServices.slice(0, 20).map((svc, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => selectService(svc)}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
                              >
                                <span className="text-gray-700">{svc.name}</span>
                                <span className="text-xs text-gray-400">{svc.category}</span>
                              </button>
                            ))}
                            {filteredServices.length === 0 && (
                              <p className="px-4 py-3 text-sm text-gray-400">No services found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g. Cape Town, Sandton, Durban"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#37b34a] transition-colors"
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 mt-2"
                >
                  {submitting ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <>
                      Register for Early Access
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center pt-1">
                  By registering, you agree to our{' '}
                  <a href="/terms" className="text-[#37b34a] hover:underline">Terms of Service</a>{' '}and{' '}
                  <a href="/privacy" className="text-[#37b34a] hover:underline">Privacy Policy</a>.
                  <br />No spam — we only email when it matters.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Star,
                title: "For Professionals",
                desc: "Get found by customers actively looking for your services. Build your reputation with reviews and grow your business."
              },
              {
                icon: Shield,
                title: "For Customers",
                desc: "Find vetted, trusted professionals with verified reviews. Compare quotes and hire with confidence."
              },
              {
                icon: Briefcase,
                title: "100+ Services",
                desc: "From plumbers to personal trainers, accountants to app developers — every professional service covered."
              }
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-[#37b34a]/10 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#37b34a]" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-100 py-6">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-400">
            {copyright}
          </p>
          <a href="/" className="text-sm text-[#37b34a] font-medium hover:underline">
            Back to {appName}
          </a>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
